import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, UtensilsCrossed, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError, money, fileUrl } from "@/lib/api";
import { Loader, EmptyState, PageHeader } from "@/components/common/Primitives";
import { ProductForm } from "./ProductForm";
import { ConfirmButton } from "@/components/common/ConfirmButton";

export default function Products() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [p, c, a, v] = await Promise.all([
      api.get("/restaurant/products"), api.get("/restaurant/categories"),
      api.get("/restaurant/addon-groups"), api.get("/restaurant/variation-groups"),
    ]);
    setData({ products: p.data, categories: c.data, addons: a.data, variations: v.data });
  };
  useEffect(() => { load(); }, []);

  if (!data) return <Loader />;
  const catName = (id) => data.categories.find((c) => c.id === id)?.name || "—";
  const list = data.products.filter(
    (p) => (!cat || p.category_id === cat) && p.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = async (p) => {
    try {
      const { data: r } = await api.patch(`/restaurant/products/${p.id}/toggle`);
      setData({ ...data, products: data.products.map((x) => (x.id === p.id ? { ...x, is_available: r.is_available } : x)) });
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (p) => {
    try { await api.delete(`/restaurant/products/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Menu items" subtitle={`${data.products.length} items across ${data.categories.length} categories`}>
        <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => setEditing({})} disabled={!data.categories.length} data-testid="product-add-btn">
          <Plus className="mr-1 h-4 w-4" />Add item
        </Button>
      </PageHeader>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative sm:w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search items" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-search" />
        </div>
        <select className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="product-category-filter">
          <option value="">All categories</option>
          {data.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {list.length === 0 ? (
        <EmptyState icon={UtensilsCrossed} title="No items" description={data.categories.length ? "Add your first dish." : "Create a category first."} testId="products-empty" />
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="products-list">
          {list.map((p) => (
            <div key={p.id} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-3" data-testid={`product-row-${p.id}`}>
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                {p.image_url && <img src={p.image_url.startsWith("/api") ? fileUrl(p.image_url) : p.image_url} alt={p.name} className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="truncate font-heading font-semibold text-slate-900">{p.name}</p>
                  <div className="flex shrink-0 gap-1">
                    <button onClick={() => setEditing(p)} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                    <ConfirmButton title={`Delete "${p.name}"?`} onConfirm={() => remove(p)} className="p-1 text-slate-400 hover:text-rose-600" testId={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></ConfirmButton>
                  </div>
                </div>
                <p className="text-xs text-slate-400">{catName(p.category_id)}{p.sku && ` · ${p.sku}`}</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-semibold text-slate-800">
                    {p.sale_price != null && p.sale_price < p.base_price ? (
                      <>{money(p.sale_price)} <s className="text-xs font-normal text-slate-400">{money(p.base_price)}</s></>
                    ) : money(p.base_price)}
                  </span>
                  <label className="flex items-center gap-2 text-xs text-slate-500">
                    {p.is_available ? "Available" : "Sold out"}
                    <Switch checked={p.is_available} onCheckedChange={() => toggle(p)} data-testid={`product-available-${p.id}`} />
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <ProductForm product={editing} lookups={data} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
